import { clipLaneToRange } from '@app/modules/persons/appearances/clip-lane-to-range';
import { byFirstAppearance } from '@app/modules/persons/appearances/order-lanes';
import {
  PackedAppearance,
  packAppearancesIntoRows,
} from '@app/modules/persons/appearances/pack-appearances-into-rows';
import { PersonLane } from '@app/modules/persons/appearances/types';
import { usePersonLanes } from '@app/modules/persons/appearances/usePersonLanes';
import { TimeRange } from '@mi-front/core';
import { createContext, useContext, useMemo } from 'react';
import { STREAM_UI_CONFIG } from '../../../../consts';
import { useFocusRange } from '../../../../stores/stream-screen.store';

export type PersonsTrackLanesValue = {
  lanes: PersonLane[];
  range: TimeRange<number>;
  packed: PackedAppearance[];
  hasPersonsInStream: boolean;
  isLoading: boolean;
};

const PersonsLanesContext = createContext<PersonsTrackLanesValue | null>(null);

/**
 * Computes focus-clipped person lanes + summary packing for the loaded stream range.
 * Used only by {@link PersonsLanesProvider}; consumers read via {@link usePersonsTrackLanes}.
 */
export function usePersonsTrackLanesState(): PersonsTrackLanesValue {
  const focusRange = useFocusRange();
  const { lanes: streamLanes, isLoading } = usePersonLanes();

  const lanes = useMemo(
    () =>
      streamLanes
        .map((l) => clipLaneToRange(l, focusRange))
        .filter((l): l is PersonLane => l !== null)
        .sort(byFirstAppearance),
    [streamLanes, focusRange.from, focusRange.to],
  );

  const packed = useMemo(
    () =>
      packAppearancesIntoRows(
        streamLanes,
        STREAM_UI_CONFIG.personsSummaryRows,
        STREAM_UI_CONFIG.personsPackingOverlapToleranceMs,
      ),
    [streamLanes],
  );

  return {
    lanes,
    range: focusRange,
    packed,
    hasPersonsInStream: streamLanes.length > 0,
    isLoading,
  };
}

export const PersonsLanesContextProvider = PersonsLanesContext.Provider;

/**
 * Person lanes for the track and Personas tab (shared via PersonsLanesProvider).
 */
export function usePersonsTrackLanes(): PersonsTrackLanesValue {
  const ctx = useContext(PersonsLanesContext);
  if (!ctx) {
    throw new Error('usePersonsTrackLanes must be used within PersonsLanesProvider');
  }
  return ctx;
}
